var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Box, ContentBlock, Inline, ButtonIcon, IconClear, IconRefresh, Text, Strong } from "braid-design-system";
import React, { useState, useEffect } from "react";
class ErrorBoundary extends React.Component {
  constructor() {
    super(...arguments);
    __publicField(this, "state", {
      hasError: false
    });
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    const { children, fallback } = this.props;
    const { hasError } = this.state;
    if (hasError) {
      return fallback;
    }
    return children;
  }
}
const VERSION_PARAM = "v";
const GBV_PARAM = "gbv";
const getQueryParam = (key) => new URLSearchParams(window.location.search).get(key) || void 0;
const isServerRendering = () => typeof window === "undefined";
const getStorageItemKey = (applicationName, key) => `${applicationName}:${key}`;
const getParamInStorage = (storageItemKey) => sessionStorage.getItem(storageItemKey);
const setParamInStorage = (storageItemKey, value) => {
  sessionStorage.setItem(storageItemKey, value);
};
const removeParamInStorage = (storageItemKey) => sessionStorage.removeItem(storageItemKey);
const setQueryParam = (key, value) => {
  const url = new URL(window.location.href);
  if (value && url.searchParams.get(key) === value) {
    return;
  }
  if (!value && !url.searchParams.has(key)) {
    return;
  }
  if (value) {
    url.searchParams.set(key, value);
  } else {
    url.searchParams.delete(key);
  }
  window.location.replace(url);
};
const persistParams = (applicationName, key) => {
  if (isServerRendering()) {
    throw new Error(
      "Attempted to persist UIV when window was not available. `persistParams` should only be called on the client."
    );
  }
  const queryVersion = getQueryParam(key);
  const storageItemKey = getStorageItemKey(applicationName, key);
  if (queryVersion) {
    setParamInStorage(storageItemKey, queryVersion);
    setQueryParam(key, queryVersion);
    return queryVersion;
  }
  const sessionVersion = getParamInStorage(storageItemKey);
  if (sessionVersion) {
    setQueryParam(key, sessionVersion);
    return sessionVersion;
  }
};
const UivBanner = ({
  applicationName,
  branch,
  position,
  version
}) => /* @__PURE__ */ jsx(ErrorBoundary, { fallback: null, children: /* @__PURE__ */ jsx(
  UivBannerComponent,
  {
    applicationName,
    branch,
    position,
    version
  }
) });
const resetQueryParam = (prefix, key, queryParamValue) => {
  const storageItemKey = getStorageItemKey(prefix, key);
  removeParamInStorage(storageItemKey);
  setQueryParam(key, queryParamValue ?? null);
};
const UivBannerComponent = ({
  applicationName,
  branch,
  position,
  version
}) => {
  const graphqlPrefix = "graphql";
  const [uiv, setUiv] = useState();
  const [gbv, setGbv] = useState();
  const [hideUivBanner, setHideUivBanner] = useState(false);
  useEffect(() => {
    const newUiv = persistParams(applicationName, VERSION_PARAM);
    if (uiv !== newUiv) {
      setUiv(newUiv);
    }
    const newGbv = persistParams(graphqlPrefix, GBV_PARAM);
    if (gbv !== newGbv) {
      setGbv(newGbv);
    }
  }, [applicationName, uiv, gbv]);
  if (!uiv && !gbv || hideUivBanner) {
    return null;
  }
  if (gbv === "reset") {
    setGbv(void 0);
    resetQueryParam(graphqlPrefix, GBV_PARAM);
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      background: "caution",
      padding: "gutter",
      position,
      zIndex: "notification",
      children: /* @__PURE__ */ jsx(ContentBlock, { children: /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "bottom", align: "center", children: [
        /* @__PURE__ */ jsx(
          ButtonIcon,
          {
            id: "hide-uiv",
            label: "Hide UIV Banner",
            icon: /* @__PURE__ */ jsx(IconClear, {}),
            tone: "neutral",
            variant: "transparent",
            onClick: () => {
              setHideUivBanner(true);
            }
          }
        ),
        Boolean(uiv) && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(LabelValueBox, { emoji: "🔨", label: "Build Number", value: version }),
          /* @__PURE__ */ jsx(LabelValueBox, { emoji: "🌲", label: "Branch", value: branch }),
          /* @__PURE__ */ jsx(LabelValueBox, { emoji: "📄", label: "UIV", value: uiv }),
          /* @__PURE__ */ jsx(
            ButtonIcon,
            {
              id: "reset-uiv",
              label: "Reset UIV",
              icon: /* @__PURE__ */ jsx(IconRefresh, {}),
              tone: "neutral",
              variant: "transparent",
              onClick: () => {
                resetQueryParam(applicationName, VERSION_PARAM);
              }
            }
          )
        ] }),
        Boolean(gbv) && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(LabelValueBox, { emoji: "📄", label: "GBV", value: gbv }),
          /* @__PURE__ */ jsx(
            ButtonIcon,
            {
              id: "reset-gbv",
              label: "Reset GBV",
              icon: /* @__PURE__ */ jsx(IconRefresh, {}),
              tone: "neutral",
              variant: "transparent",
              onClick: () => {
                resetQueryParam(graphqlPrefix, GBV_PARAM, "reset");
              }
            }
          )
        ] })
      ] }) })
    }
  );
};
const LabelValueBox = ({ label, emoji, value }) => /* @__PURE__ */ jsxs(Text, { children: [
  emoji,
  " ",
  /* @__PURE__ */ jsxs(Strong, { children: [
    label,
    ":"
  ] }),
  " ",
  value
] });
const getIsNVLEnabled = (defaultIsNVLEnabledValue) => {
  var _a;
  if (typeof window === "undefined") {
    return defaultIsNVLEnabledValue;
  }
  try {
    const allCookies = `; ${document.cookie}`;
    const parts = allCookies.split("; NVLFLAG=");
    if (parts.length === 2) {
      const cookieValue = ((_a = parts.pop()) == null ? void 0 : _a.split(";").shift()) || null;
      const isNVLCookieEnabled = cookieValue === "true";
      if (isNVLCookieEnabled) {
        return true;
      }
    }
  } catch {
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const isNVLQueryStringEnabled = params.get("nvl") !== null;
    if (isNVLQueryStringEnabled) {
      return true;
    }
  } catch {
  }
  return defaultIsNVLEnabledValue;
};
const useIsNVLEnabled = (defaultIsNVLEnabledValue = false) => {
  const [isEnabled, setIsEnabled] = useState(defaultIsNVLEnabledValue);
  useEffect(
    () => setIsEnabled(Boolean(getIsNVLEnabled(defaultIsNVLEnabledValue))),
    [defaultIsNVLEnabledValue]
  );
  return isEnabled;
};
export {
  UivBanner,
  getQueryParam,
  useIsNVLEnabled
};
